import { addMessages, init, locale } from 'svelte-i18n';
import en from './en.json';
import es from './es.json';
import ar from './ar.json';

export const SUPPORTED_LOCALES = ['en', 'es', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const RTL_LOCALES = new Set<SupportedLocale>(['ar']);

// Register all messages and init with 'en' fallback at module level so that
// $t() works during SSR prerendering. The locale is updated to the user's
// saved preference in +layout.svelte's onMount (browser-only).
let _initialized = false;
function _bootstrap() {
	if (_initialized) return;
	_initialized = true;
	addMessages('en', en);
	addMessages('es', es);
	addMessages('ar', ar);
	init({ fallbackLocale: 'en', initialLocale: 'en' });
}
_bootstrap();

export function initI18n(): void {
	const saved =
		typeof localStorage !== 'undefined'
			? (localStorage.getItem('grafem-language') ?? 'en')
			: 'en';

	const initialLocale = SUPPORTED_LOCALES.includes(saved as SupportedLocale) ? saved : 'en';

	locale.set(initialLocale);
}

export function setLocale(lang: string): void {
	if (!SUPPORTED_LOCALES.includes(lang as SupportedLocale)) return;
	locale.set(lang);
	localStorage.setItem('grafem-language', lang);
	applyLocaleToDocument(lang as SupportedLocale);
}

export function applyLocaleToDocument(lang: SupportedLocale): void {
	if (typeof document === 'undefined') return;
	document.documentElement.lang = lang;
	document.documentElement.dir = RTL_LOCALES.has(lang) ? 'rtl' : 'ltr';
}

export function getSavedLocale(): string {
	if (typeof localStorage === 'undefined') return 'en';
	const saved = localStorage.getItem('grafem-language') ?? 'en';
	return SUPPORTED_LOCALES.includes(saved as SupportedLocale) ? saved : 'en';
}
